import * as React from "react";
import { Button, Modal, Label } from "semantic-ui-react";

import { TimePeriod } from "@teamprevent/common/calendar";

import { Calendar } from "..";
import { Authorize } from "../../auth/Authorize";
import { ADMIN_ROLES } from "../../auth";

interface Clinician {
    id: string;
    name: string;
}

interface Props {
    selectedClinicians: Clinician[];
    conflicts?: string[] | false;
    highlight: TimePeriod;
    selectorLimitedToTeamPreventUserIds?: string[];
    onCliniciansChanged: (clinicians: Clinician[]) => void;
    restrictToOneClinician?: boolean;
}

const RemoveClinicianButton = ({ onRemove, clinician }: { clinician: Clinician; onRemove: (clinician: Clinician) => void }) => {
    const onClick = React.useCallback(() => {
        onRemove(clinician);
    }, [clinician, onRemove]);
    return <Button className="remove-clinician-button" icon="delete" onClick={onClick} />;
};

export const ClinicianSelector = ({ selectedClinicians, onCliniciansChanged, ...props }: Props) => {
    const [open, setOpen] = React.useState(false);
    const onOpen = React.useCallback(() => setOpen(true), []);
    const onClose = React.useCallback(() => setOpen(false), []);
    const onClinicianSelected = React.useCallback((id: string, name: string) => {
        if (!selectedClinicians.find(clinician => clinician.id === id)) {
            onCliniciansChanged([...selectedClinicians, { id, name }]);
        }

        onClose();
    }, [onCliniciansChanged, selectedClinicians, onClose]);
    const onClinicianRemoved = React.useCallback((clinician: Clinician) => {
        onCliniciansChanged(selectedClinicians.filter(c => clinician.id !== c.id));
    }, [onCliniciansChanged, selectedClinicians]);
    return (
        <div className="clinician-selector">
            <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN, ADMIN_ROLES.SENIOR_ADMINISTRATOR, ADMIN_ROLES.ADMINISTRATOR]}>
                <Button
                    className="clinician-selector-button"
                    as="a"
                    content="Add Clinician"
                    onClick={onOpen}
                />
            </Authorize>
            {selectedClinicians?.map(clinician =>
                (<div key={clinician.id}>
                    <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN, ADMIN_ROLES.SENIOR_ADMINISTRATOR, ADMIN_ROLES.ADMINISTRATOR]}>
                        <RemoveClinicianButton clinician={clinician} onRemove={onClinicianRemoved} />
                    </Authorize>
                    {clinician.name}
                    {props.conflicts && props.conflicts.find(conflict => conflict === clinician.id) &&
                        <Label basic color="red" pointing="left" content={"Conflicts with another appointment"} />
                    }
                </div>)
            )}
            {props.restrictToOneClinician && selectedClinicians?.length > 1
                && <Label basic color="red" pointing="above" content={"Only one clinician can be selected"} />}
            <Modal open={open} size="fullscreen">
                <Modal.Content>
                    <Calendar
                        teamPreventUserSelector
                        onEntitySelected={onClinicianSelected}
                        highlight={props.highlight}
                        selectorLimitedToTeamPreventUserIds={props.selectorLimitedToTeamPreventUserIds}
                        selectedUserIds={selectedClinicians?.map(sc => sc.id)}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button as="a" content="Cancel" onClick={onClose} />
                </Modal.Actions>
            </Modal>
        </div>
    );
};
